import { Box, Button, Link, Stack } from '@mui/material';
import { includes } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { payoutOrderAutomationApi, payoutOrdersApi } from 'api';
import {
  CallbackUrlStatusLabel,
  CloseDialogResult,
  ConfirmButton,
  ContactInfo,
  CopyText,
  DataWrapper,
  DateLabel,
  DescriptionRow,
  Money,
  OrderAgentCashback,
  PayoutOrderStatusLabel,
  RequisitesInfo,
  OrderReceiptsValidation,
} from 'components';
import {
  OrderType,
  QueryKey,
  PayoutOrderStatus,
  PayoutOrderStatusDetails,
  WebhookMethod,
  PayoutOrderAutomationStatus,
  RequisitesCascadeMode,
} from 'enums';
import { MerchantRequests } from 'features/orders/MerchantRequests';
import { OrderWebhooksLink } from 'features/orders/OrderWebhooksLink';
import { P2PProvidersRequests } from 'features/orders/P2PProvidersRequests';
import { useMeta, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  PayoutOrder,
  PayoutOrderDispute,
  PayoutOrderReject,
  ResolvePayoutOrder,
  ResolvePayoutOrderDispute,
} from 'types';
import { formatUtils, orderUtils } from 'utils';

import { AutomationRequests } from '../AutomationRequests';
import { PayoutOrderAutomationStatusIcon } from '../columns';
import { DisputeDialog } from '../DisputeDialog';
import { P2PProviderPayoutOrderDetails } from '../P2PProviderPayoutOrderDetails';
import { PayoutOrderStatusHistory } from '../PayoutOrderStatusHistory';
import { ReceiptsModal } from '../ReceiptsModal';
import { RejectDialog } from '../RejectDialog';
import { ResolveDisputeDialog } from '../ResolveDisputeDialog';
import { ResolveOrderDialog } from '../ResolveOrderDialog';

type Props = {
  order: PayoutOrder;
  loading?: boolean;
  onAccept: () => void;
  onDispute: (data: PayoutOrderDispute) => void;
  onCancelCompleted: () => void;
  onResolveDispute: (data: ResolvePayoutOrderDispute) => void;
  onResolveOrder: (data: ResolvePayoutOrder) => void;
  onConfirm: () => void;
  onReject: (data: PayoutOrderReject) => void;
  onReleaseHold?: () => void;
  onUnlock?: () => void;
  onNextAssignee?: () => void;
  onRequeryProviders?: () => void;
};

export const PayoutOrderDetails: React.FC<Props> = ({
  order,
  loading,
  onAccept,
  onDispute,
  onCancelCompleted,
  onResolveDispute,
  onResolveOrder,
  onConfirm,
  onReject,
  onReleaseHold,
  onUnlock,
  onNextAssignee,
  onRequeryProviders,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders',
  });
  const { t: tColumns } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.table.columns',
  });
  const { t: tLock } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.payout_orders.labels',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const { traderCanOpenDispute } = useMeta();

  const [receiptsModalOpen, setReceiptsModalOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [resolveDisputeDialogOpen, setResolveDisputeDialogOpen] =
    useState(false);
  const [resolveOrderDialogOpen, setResolveOrderDialogOpen] = useState(false);

  const {
    isAdmin,
    isTrader,
    isTechOperator,
    isMerchant,
    isManager,
    isAdminOrTechOperator,
    role,
  } = useUser();

  const showTraderSensitiveInfo = useMemo(
    () =>
      ![PayoutOrderStatus.Requisites, PayoutOrderStatus.TraderAccept].includes(
        order.status,
      ) || !isTrader,
    [order, isTrader],
  );

  const {
    isRequisitesStatus,
    isTraderAcceptStatus,
    isTraderPaymentStatus,
    isDisputeStatus,
    isCancelledStatus,
    isCompletedStatus,
  } = useMemo(
    () => ({
      isRequisitesStatus: order?.status === PayoutOrderStatus.Requisites,
      isTraderAcceptStatus: order?.status === PayoutOrderStatus.TraderAccept,
      isTraderPaymentStatus: order?.status === PayoutOrderStatus.TraderPayment,
      isDisputeStatus: order?.status === PayoutOrderStatus.Dispute,
      isCancelledStatus: order?.status === PayoutOrderStatus.Cancelled,
      isCompletedStatus: order?.status === PayoutOrderStatus.Completed,
    }),
    [order],
  );

  const handleReceiptsClick = useCallback(() => {
    setReceiptsModalOpen(true);
  }, []);

  const handleReceiptsModalClose = useCallback(() => {
    setReceiptsModalOpen(false);
  }, []);

  const handleRejectClick = useCallback(() => {
    setRejectDialogOpen(true);
  }, []);

  const handleRejectDialogClose = useCallback(
    (result: CloseDialogResult<PayoutOrderReject>) => {
      if (result.ok && result.data) {
        onReject?.(result.data);
      }
      setRejectDialogOpen(false);
    },
    [onReject],
  );

  const handleDisputeClick = useCallback(() => {
    setDisputeDialogOpen(true);
  }, []);

  const handleDisputeDialogClose = useCallback(
    (result: CloseDialogResult<PayoutOrderDispute>) => {
      if (result.ok && result.data) {
        onDispute?.(result.data);
      }
      setDisputeDialogOpen(false);
    },
    [onDispute],
  );

  const handleResolveDisputeClick = useCallback(() => {
    setResolveDisputeDialogOpen(true);
  }, []);

  const handleResolveOrderClick = useCallback(() => {
    setResolveOrderDialogOpen(true);
  }, []);

  const handleResolveDisputeDialogClose = useCallback(
    (result: CloseDialogResult<ResolvePayoutOrderDispute>) => {
      if (result.ok && result.data) {
        onResolveDispute?.(result.data);
      }
      setResolveDisputeDialogOpen(false);
    },
    [onResolveDispute],
  );

  const handleResolveOrderDialogClose = useCallback(
    (result: CloseDialogResult<ResolvePayoutOrder>) => {
      if (result.ok && result.data) {
        onResolveOrder?.(result.data);
      }
      setResolveOrderDialogOpen(false);
    },
    [onResolveOrder],
  );

  const totalCascadeAssigneeTimeoutExpired = useMemo(() => {
    if (!order.totalCascadeAssigneeTimeoutAt) {
      return false;
    }

    return new Date(order.totalCascadeAssigneeTimeoutAt) < new Date();
  }, [order]);

  const statusHistoryQueryKey = useMemo(
    () => [QueryKey.PayoutOrderStatusHistory, order?.id],
    [order],
  );

  const canSeeStatusHistory = useMemo(
    () => isManager || (isTrader && showTraderSensitiveInfo),
    [isManager, isTrader, showTraderSensitiveInfo],
  );

  const statusHistoryQueryResult = useQuery(
    statusHistoryQueryKey,
    () => payoutOrdersApi.getPayoutOrderStatusHistoryAsRole(role)(order.id),
    { enabled: !!order?.id && canSeeStatusHistory },
  );

  const orderAutomationQueryKey = useMemo(
    () => [QueryKey.PayoutOrderAutomations, order?.orderAutomationId],
    [order],
  );

  const orderAutomationQueryResult = useQuery(
    orderAutomationQueryKey,
    () => payoutOrderAutomationApi.getOne(order.orderAutomationId),
    {
      enabled:
        !!order?.orderAutomationId && (isAdminOrTechOperator || isTrader),
    },
  );

  const requisitesAutomationWallet = useMemo(
    () => orderAutomationQueryResult?.data?.requisitesAutomation?.wallet,
    [orderAutomationQueryResult?.data],
  );

  const currentCascadeAssigneeEnabled = useMemo(
    () => isManager && !!order.currentCascadeAssigneeId,
    [isManager, order.currentCascadeAssigneeId],
  );

  const nextCascadeAssigneeEnabled = useMemo(
    () =>
      !totalCascadeAssigneeTimeoutExpired &&
      order.status === PayoutOrderStatus.Dispute &&
      !!order.currentCascadeAssigneeId &&
      includes(
        [
          PayoutOrderStatusDetails.DisputeCascadeAssigneeTimeout,
          PayoutOrderStatusDetails.DisputeUnresolvedResponse,
        ],
        order.statusDetails,
      ),
    [
      order.status,
      order.statusDetails,
      order.currentCascadeAssigneeId,
      totalCascadeAssigneeTimeoutExpired,
    ],
  );

  const currentCascadeAssigneeQueryResult = useQuery(
    [QueryKey.PayoutOrderCurrentCascadeAssignee, order?.id],
    () => payoutOrdersApi.getCurrentCascadeAssigneeAsRole(role)(order.id),
    {
      enabled: currentCascadeAssigneeEnabled,
    },
  );

  return (
    <Fragment>
      <DataWrapper isLoading={loading}>
        <Stack direction="column" spacing={6}>
          <Stack direction="column" spacing={3}>
            <DescriptionRow title={tColumns('id')} value={order.id} canCopy />
            <DescriptionRow
              title={tColumns('status')}
              value={
                <Fragment>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PayoutOrderStatusLabel status={order?.status} />
                    {!isMerchant && (
                      <Box ml={2}>
                        <PayoutOrderAutomationStatusIcon order={order} />
                      </Box>
                    )}
                  </Box>
                  {order.statusDetails && (
                    <div>
                      {orderUtils.getPayoutStatusDetailsLabel(
                        order.statusDetails,
                      )}
                    </div>
                  )}
                </Fragment>
              }
            />
            {!isTrader && order.holdTraderAsset && order.holdTimeoutAt && (
              <DescriptionRow
                title={tColumns('trader_asset_waiting_release')}
                value={
                  <DateLabel>
                    {formatUtils.formatDate(order.holdTimeoutAt)}
                  </DateLabel>
                }
              />
            )}
            {isAdminOrTechOperator && order.lock && (
              <DescriptionRow
                title={tLock('locked')}
                value={
                  <Box sx={{ color: 'error.main', fontWeight: 'bold' }}>
                    {order.lock.lockReason && (
                      <Box sx={{ fontWeight: 'normal' }}>
                        {tLock(`lock_reason.${order.lock.lockReason}`, {
                          defaultValue: order.lock.lockReason,
                        })}
                      </Box>
                    )}
                    <Box sx={{ fontWeight: 'normal' }}>
                      <DateLabel>
                        {formatUtils.formatDate(order.lock.createdAt)}
                      </DateLabel>
                    </Box>
                    {onUnlock && (
                      <ConfirmButton
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={loading}
                        onConfirm={onUnlock}
                        sx={{ mt: 1 }}
                      >
                        {tLock('unlock')}
                      </ConfirmButton>
                    )}
                  </Box>
                }
              />
            )}
            {showTraderSensitiveInfo && (
              <DescriptionRow
                title={tColumns('amount')}
                value={
                  <Fragment>
                    <Money
                      value={order.amount}
                      fiatCurrencyId={order.fiatCurrencyId}
                      symbol
                    />
                    <div>
                      <Money
                        value={order.assetCurrencyAmount}
                        assetCurrencyId={order.assetCurrencyId}
                        symbol
                      />
                      {' ('}
                      <Money
                        value={order.currencyRate}
                        fiatCurrencyId={order.fiatCurrencyId}
                        symbol
                      />
                      {')'}
                    </div>
                  </Fragment>
                }
              />
            )}
            {isManager && (
              <DescriptionRow
                title={tColumns('customer')}
                value={
                  <Fragment>
                    {order.customerId && <CopyText text={order.customerId} />}
                    <div>{order.customerName}</div>
                    <ContactInfo
                      phone={order.customerPhone}
                      email={order.customerEmail}
                    />
                  </Fragment>
                }
              />
            )}
            <DescriptionRow
              title={tColumns('customer_requisites')}
              value={
                <RequisitesInfo
                  cardholder={order.customerCardholder}
                  bankId={order.bankId}
                  paymentTypeId={order.paymentTypeId}
                  cardInfo={order.customerCard}
                  phone={order.customerPhone}
                  swiftBic={order.customerSwiftBic}
                  bic={order.customerBic}
                  idCard={order.customerIdCard}
                  beneficiaryName={order.customerBeneficiaryName}
                  accountNumber={order.customerAccountNumber}
                  email={order.customerEmail}
                  expirationDate={order.customerExpirationDate}
                  taxId={order.customerTaxId}
                />
              }
            />
            {isManager && order.requisites && (
              <DescriptionRow
                title={tColumns('trader_requisites')}
                value={
                  <RequisitesInfo
                    bankId={order.requisites.bankId}
                    paymentTypeId={order.requisites.paymentTypeId}
                    fiatCurrencyId={order.requisites.fiatCurrencyId}
                  />
                }
              />
            )}
            {isManager && (
              <Fragment>
                <DescriptionRow
                  title={tColumns('shop')}
                  value={
                    <Fragment>
                      <CopyText text={order.shopId} />
                      <div>{formatUtils.formatName(order.shop)}</div>
                    </Fragment>
                  }
                />
                <DescriptionRow
                  title={tColumns('merchant')}
                  value={
                    <Fragment>
                      <CopyText text={order.merchantId} />
                      <div>{order.merchant.user?.name}</div>
                    </Fragment>
                  }
                />
              </Fragment>
            )}

            {(isManager || isMerchant) && (
              <Fragment>
                {order.externalOrderId && (
                  <DescriptionRow
                    title={tColumns('external_order_id')}
                    value={
                      <Fragment>
                        <CopyText text={order.externalOrderId} />
                      </Fragment>
                    }
                  />
                )}
                {order.callbackUrl && (
                  <DescriptionRow
                    title={tColumns('callback')}
                    value={
                      <Fragment>
                        <div className="tw-flex">
                          <CopyText
                            className="tw-break-all"
                            text={order.callbackUrl}
                            label={`[${
                              order.callbackMethod || WebhookMethod.Post
                            }] ${order.callbackUrl}`}
                          />
                        </div>
                        {order.callbackUrlStatus && (
                          <div>
                            <CallbackUrlStatusLabel
                              status={order.callbackUrlStatus}
                            />
                          </div>
                        )}
                      </Fragment>
                    }
                  />
                )}
              </Fragment>
            )}
            {isMerchant && (
              <DescriptionRow
                title={tColumns('payout_shop_amount')}
                value={
                  <Money
                    value={order.shopAmount}
                    assetCurrencyId={order.assetCurrencyId}
                    symbol
                  />
                }
              />
            )}
            {!isMerchant &&
              showTraderSensitiveInfo &&
              !order.p2pProviderOrderId && (
                <DescriptionRow
                  title={tColumns('trader_compensation')}
                  value={
                    <Money
                      value={order.traderCompensation}
                      assetCurrencyId={order.assetCurrencyId}
                      symbol
                    />
                  }
                />
              )}
            {isManager && (
              <Fragment>
                {!!order?.shopFee && (
                  <DescriptionRow
                    title={tColumns('shop_fee')}
                    value={
                      <Money
                        value={order.shopFee}
                        assetCurrencyId={order.assetCurrencyId}
                        symbol
                      />
                    }
                  />
                )}
              </Fragment>
            )}
            {(isAdmin || isTechOperator) && (
              <Fragment>
                {!!order.expectedProviderFeeAmount && (
                  <DescriptionRow
                    title={t('table.columns.p2p_provider_fee')}
                    value={
                      <div>
                        <div
                          className="tw-flex"
                          key={t('table.columns.expected_provider_fee_amount')}
                        >
                          <div className="tw-mr-2">
                            {t('table.columns.expected_provider_fee_amount')}
                          </div>
                          <Money
                            value={order.expectedProviderFeeAmount || 0}
                            assetCurrencyId={order.assetCurrencyId}
                            symbol
                          />
                        </div>
                        <div
                          className="tw-flex"
                          key={t('table.columns.actual_provider_fee_amount')}
                        >
                          <div className="tw-mr-2">
                            {t('table.columns.actual_provider_fee_amount')}
                          </div>
                          <Money
                            value={order.actualProviderFeeAmount || 0}
                            assetCurrencyId={
                              order?.p2pProviderOrder?.assetCurrencyId ||
                              order.assetCurrencyId
                            }
                            symbol
                          />
                        </div>
                      </div>
                    }
                  />
                )}
                <OrderAgentCashback
                  order={order}
                  operation={OrderType.Payout}
                />
              </Fragment>
            )}
            {(isManager || isTrader) && order.orderAutomationId && (
              <DescriptionRow
                title={tColumns('automation')}
                value={
                  <Fragment>
                    <CopyText text={order.orderAutomationId} />
                    {requisitesAutomationWallet && (
                      <div>
                        {`${tColumns('wallet')}: ${requisitesAutomationWallet}`}
                      </div>
                    )}
                  </Fragment>
                }
              />
            )}
            {orderUtils.isTraderPaid(order, statusHistoryQueryResult.data) && (
              <DescriptionRow
                title={tColumns('payment_date')}
                value={
                  <Fragment>
                    {order.requisites && (
                      <RequisitesInfo
                        bankId={order.requisites.bankId}
                        fiatCurrencyId={order.requisites.fiatCurrencyId}
                        paymentTypeId={order.requisites.paymentTypeId}
                      />
                    )}
                    <Fragment>
                      <DateLabel>
                        {formatUtils.formatDate(
                          orderUtils.getStatusDate(
                            order,
                            PayoutOrderStatus.Completed,
                            statusHistoryQueryResult.data,
                          ),
                        )}
                      </DateLabel>
                    </Fragment>
                  </Fragment>
                }
              />
            )}
            {(!isTrader || (!isTraderAcceptStatus && !isRequisitesStatus)) && (
              <DescriptionRow
                title={tColumns('receipts')}
                value={
                  <div>
                    <Link
                      component="button"
                      variant="body2"
                      underline="none"
                      onClick={handleReceiptsClick}
                    >
                      {tCommon('buttons.open')}
                    </Link>
                  </div>
                }
              />
            )}
            {isManager && (
              <P2PProvidersRequests
                orderId={order.id}
                orderType={OrderType.Payout}
              />
            )}
            {isManager && (
              <MerchantRequests
                orderId={order.id}
                orderType={OrderType.Payout}
              />
            )}
            {isAdminOrTechOperator && (
              <OrderWebhooksLink
                orderId={order.id}
                orderType={OrderType.Payout}
                createdAt={order.createdAt}
              />
            )}
            {isManager && <AutomationRequests orderId={order.id} />}
            {order.p2pProviderOrderId && (
              <P2PProviderPayoutOrderDetails order={order} />
            )}
            {isManager &&
              (isDisputeStatus || isCancelledStatus || isCompletedStatus) && (
                <DescriptionRow
                  title={tColumns('validation')}
                  value={<OrderReceiptsValidation order={order} labelled />}
                />
              )}
            {canSeeStatusHistory && (
              <PayoutOrderStatusHistory
                order={order}
                loading={loading}
                statusHistoryQueryResult={statusHistoryQueryResult}
              />
            )}
          </Stack>

          {isTrader &&
            (isTraderAcceptStatus || isRequisitesStatus) &&
            !orderUtils.isMaxTimeoutOver(order) && (
              <Stack direction="column" spacing={3}>
                {isTraderAcceptStatus && (
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={loading}
                    onClick={handleRejectClick}
                    fullWidth
                  >
                    {tCommon('buttons.reject')}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={loading}
                  onClick={onAccept}
                  fullWidth
                >
                  {tCommon('buttons.accept')}
                </Button>
              </Stack>
            )}

          {isTrader && isTraderPaymentStatus && (
            <Stack direction="column" spacing={3}>
              {traderCanOpenDispute && (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={loading}
                  onClick={handleDisputeClick}
                  fullWidth
                >
                  {t('buttons.dispute')}
                </Button>
              )}
              {(!order.orderAutomationStatus ||
                order.orderAutomationStatus ===
                  PayoutOrderAutomationStatus.Rejected) && (
                <Fragment>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={loading}
                    onClick={handleRejectClick}
                    fullWidth
                  >
                    {tCommon('buttons.reject')}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={loading}
                    onClick={onConfirm}
                    fullWidth
                  >
                    {tCommon('buttons.confirm')}
                  </Button>
                </Fragment>
              )}
            </Stack>
          )}
          {isTrader &&
            isDisputeStatus &&
            includes(
              [
                PayoutOrderStatusDetails.DisputePaymentTimeout,
                PayoutOrderStatusDetails.DisputePaymentFailed,
                PayoutOrderStatusDetails.DisputeInvalidRequisites,
              ],
              order?.statusDetails,
            ) && (
              <Stack direction="column" spacing={3}>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={loading}
                  onClick={handleResolveDisputeClick}
                  fullWidth
                >
                  {t('buttons.resolve_dispute')}
                </Button>
              </Stack>
            )}
          {isManager && isTraderPaymentStatus && (
            <Stack direction="column" spacing={3}>
              <Button
                variant="outlined"
                color="error"
                disabled={loading}
                onClick={handleDisputeClick}
                fullWidth
              >
                {t('buttons.dispute')}
              </Button>
            </Stack>
          )}
          {isManager && isDisputeStatus && (
            <Stack direction="column" spacing={3}>
              <Button
                variant="outlined"
                color="primary"
                disabled={loading}
                onClick={handleResolveDisputeClick}
                fullWidth
              >
                {t('buttons.resolve_dispute')}
              </Button>
            </Stack>
          )}
          {isManager && isCompletedStatus && (
            <Stack direction="column" spacing={3}>
              {order.holdTraderAsset && onReleaseHold && (
                <ConfirmButton
                  variant="outlined"
                  color="primary"
                  disabled={loading}
                  onConfirm={onReleaseHold}
                  fullWidth
                >
                  {t('buttons.release_hold')}
                </ConfirmButton>
              )}
              <ConfirmButton
                variant="outlined"
                color="error"
                disabled={loading}
                onConfirm={onCancelCompleted}
                fullWidth
              >
                {tCommon('buttons.cancel')}
              </ConfirmButton>
            </Stack>
          )}
          {isAdminOrTechOperator &&
            !order.p2pProviderOrderId &&
            !order.orderAutomationId &&
            (isTraderPaymentStatus || isRequisitesStatus) && (
              <Stack direction="column" spacing={3}>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={loading}
                  onClick={handleResolveOrderClick}
                  fullWidth
                >
                  {t('buttons.resolve_order')}
                </Button>
                {isRequisitesStatus &&
                  !order.traderId &&
                  !order.currentCascadeAssigneeId &&
                  order.shop?.cascade?.payoutCascadeMode !==
                    RequisitesCascadeMode.TradersOnly &&
                  onRequeryProviders && (
                    <ConfirmButton
                      variant="outlined"
                      color="secondary"
                      disabled={loading}
                      fullWidth
                      onConfirm={onRequeryProviders}
                    >
                      {t('buttons.requery_providers')}
                    </ConfirmButton>
                  )}
              </Stack>
            )}
          {currentCascadeAssigneeEnabled && (
            <DataWrapper queryResult={currentCascadeAssigneeQueryResult}>
              <Stack direction="column" spacing={3}>
                <DescriptionRow
                  title={tCommon(
                    'features.cascades.manage.fields.assignee_type',
                  )}
                  value={
                    tCommon(
                      `features.cascades.cascade_details.assignee_types.${order.currentCascadeAssigneeType!}`,
                    ) as string
                  }
                />
                {currentCascadeAssigneeQueryResult.data?.trader?.user?.name && (
                  <DescriptionRow
                    title={tCommon('features.cascades.manage.fields.trader')}
                    value={
                      currentCascadeAssigneeQueryResult.data.trader.user.name
                    }
                  />
                )}
                {currentCascadeAssigneeQueryResult.data?.p2pProvider?.name && (
                  <DescriptionRow
                    title={tCommon(
                      'features.cascades.cascade_details.assignee_types.p2p_provider',
                    )}
                    value={
                      currentCascadeAssigneeQueryResult.data?.p2pProvider?.name
                    }
                  />
                )}
                {nextCascadeAssigneeEnabled && onNextAssignee && (
                  <ConfirmButton
                    variant="outlined"
                    color="warning"
                    disabled={loading}
                    fullWidth
                    onConfirm={onNextAssignee}
                  >
                    {t('buttons.next_assignee')}
                  </ConfirmButton>
                )}
              </Stack>
            </DataWrapper>
          )}
        </Stack>
      </DataWrapper>
      <ReceiptsModal
        open={receiptsModalOpen}
        order={order}
        onClose={handleReceiptsModalClose}
      />
      <RejectDialog
        open={rejectDialogOpen}
        order={order}
        onClose={handleRejectDialogClose}
      />
      <DisputeDialog
        open={disputeDialogOpen}
        order={order}
        onClose={handleDisputeDialogClose}
      />
      <ResolveDisputeDialog
        open={resolveDisputeDialogOpen}
        order={order}
        disabled={isTrader}
        onClose={handleResolveDisputeDialogClose}
      />
      {isAdminOrTechOperator && (
        <ResolveOrderDialog
          open={resolveOrderDialogOpen}
          order={order}
          onClose={handleResolveOrderDialogClose}
        />
      )}
    </Fragment>
  );
};
